// ============================================================
// PipelineFunnel  Shared pipeline stage visualisation
// Used in Manager, Admin, and Executive dashboards
// ============================================================
import React, { useEffect, useState, useCallback } from "react";
import toast from "react-hot-toast";
import API from "../utils/api";

const STAGES = [
  { key: "visited",           label: "Visited",           icon: "", color: "#64748b", bg: "#f1f5f9" },
  { key: "interested",        label: "Interested",        icon: "", color: "#1d4ed8", bg: "#dbeafe" },
  { key: "proposal_sent",     label: "Proposal Sent",     icon: "", color: "#c2410c", bg: "#ffedd5" },
  { key: "mou_shared",        label: "MOU Shared",        icon: "", color: "#7c3aed", bg: "#ede9fe" },
  { key: "mou_signed",        label: "MOU Signed",        icon: "", color: "#4f46e5", bg: "#e0e7ff" },
  { key: "payment_requested", label: "Payment Requested", icon: "", color: "#b45309", bg: "#fef3c7" },
  { key: "closed_won",        label: "Closed  Won",      icon: "", color: "#15803d", bg: "#dcfce7" },
  { key: "closed_lost",       label: "Closed  Lost",     icon: "", color: "#dc2626", bg: "#fee2e2" },
];

// Active funnel stages (exclude closed for the funnel visual)
const FUNNEL_STAGES = STAGES.filter(s => s.key !== "closed_lost");

function fmtValue(v) {
  if (!v) return null;
  const n = parseFloat(v);
  if (n >= 10000000) return `${(n/10000000).toFixed(1)}Cr`;
  if (n >= 100000)   return `${(n/100000).toFixed(1)}L`;
  if (n >= 1000)     return `${(n/1000).toFixed(1)}K`;
  return `${n.toLocaleString("en-IN")}`;
}

export default function PipelineFunnel({ accent = "#E31E24" }) {
  const [funnel, setFunnel]   = useState([]);
  const [intelligence, setIntelligence] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView]       = useState("funnel"); // "funnel" | "table"

  const fetchFunnel = useCallback(async () => {
    setLoading(true);
    try {
      const [funnelResponse, intelligenceResponse] = await Promise.all([
        API.get("/leads/pipeline/funnel"),
        API.get("/leads/pipeline/intelligence"),
      ]);
      setFunnel(funnelResponse.data);
      setIntelligence(intelligenceResponse.data);
    } catch {
      toast.error("Failed to load pipeline data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchFunnel(); }, [fetchFunnel]);

  if (loading) return <div style={{ textAlign: "center", color: "#94a3b8", padding: 40 }}>Loading pipeline</div>;

  // Build lookup map
  const stageMap = Object.fromEntries(funnel.map(f => [f.pipeline_stage, f]));

  // Totals
  const totalActive = funnel.reduce((a, f) => a + parseInt(f.active_count || 0), 0);
  const totalWon    = parseInt(stageMap["closed_won"]?.active_count || 0);
  const totalLost   = parseInt(stageMap["closed_lost"]?.active_count || 0);
  const totalValue  = funnel.reduce((a, f) => a + parseFloat(f.total_value || 0), 0);
  const wonValue    = parseFloat(stageMap["closed_won"]?.total_value || 0);

  // Max count for bar widths
  const maxCount = Math.max(...FUNNEL_STAGES.map(s => parseInt(stageMap[s.key]?.active_count || 0)), 1);

  return (
    <div>
      {/* Summary tiles */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 20 }}>
        {[
          { label: "Active Leads",  value: totalActive, icon: "", color: accent     },
          { label: "Won",           value: totalWon,    icon: "", color: "#15803d"  },
          { label: "Lost",          value: totalLost,   icon: "", color: "#dc2626"  },
          { label: "Won Value",     value: fmtValue(wonValue) || "", icon: "", color: "#15803d", small: true },
        ].map(s => (
          <div key={s.label} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "14px 10px", textAlign: "center" }}>
            <div style={{ fontSize: 20, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: s.small ? 16 : 22, fontWeight: 900, color: s.color, lineHeight: 1.1 }}>{s.value}</div>
            <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 600, marginTop: 2 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* View toggle */}
      <div style={{ display: "flex", background: "#f1f5f9", borderRadius: 12, padding: 4, marginBottom: 16, gap: 4 }}>
        {[["funnel","Funnel"],["table","Stages"],["intelligence","Health & Forecast"]].map(([k,l]) => (
          <button key={k} onClick={() => setView(k)}
            style={{ flex: 1, padding: "9px 4px", borderRadius: 10, border: "none", fontFamily: "inherit", fontWeight: 700, fontSize: 12, cursor: "pointer",
              background: view === k ? "#fff" : "transparent",
              color: view === k ? "#1e293b" : "#64748b",
              boxShadow: view === k ? "0 1px 4px rgba(0,0,0,0.1)" : "none" }}>
            {l}
          </button>
        ))}
      </div>

      {/* FUNNEL VIEW */}
      {view === "funnel" && (
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 16, padding: "20px 16px" }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 16 }}> Pipeline Funnel  Active Leads</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {FUNNEL_STAGES.map((s, idx) => {
              const data  = stageMap[s.key] || {};
              const count = parseInt(data.active_count || 0);
              const widthPct = maxCount > 0 ? Math.max((count / maxCount) * 100, count > 0 ? 6 : 0) : 0;
              const value = data.total_value ? fmtValue(data.total_value) : null;

              // Conversion rate from previous stage
              let convRate = null;
              if (idx > 0) {
                const prevStage = FUNNEL_STAGES[idx - 1];
                const prevCount = parseInt(stageMap[prevStage.key]?.active_count || 0);
                if (prevCount > 0) convRate = Math.round((count / prevCount) * 100);
              }

              return (
                <div key={s.key}>
                  {/* Conversion arrow between stages */}
                  {idx > 0 && convRate !== null && (
                    <div style={{ display: "flex", alignItems: "center", marginBottom: 6, paddingLeft: 8 }}>
                      <div style={{ width: 1, height: 16, background: "#e2e8f0", marginLeft: 16 }} />
                      <div style={{ fontSize: 10, color: convRate >= 50 ? "#16a34a" : convRate >= 25 ? "#b45309" : "#dc2626", fontWeight: 700, marginLeft: 8 }}>
                         {convRate}% converted
                      </div>
                    </div>
                  )}

                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {/* Stage icon */}
                    <div style={{ width: 34, height: 34, borderRadius: 10, background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>
                      {s.icon}
                    </div>

                    {/* Bar + label */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#1e293b" }}>{s.label}</div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {value && <div style={{ fontSize: 10, color: "#64748b" }}>{value}</div>}
                          <div style={{ fontSize: 16, fontWeight: 900, color: count > 0 ? s.color : "#cbd5e1" }}>{count}</div>
                        </div>
                      </div>
                      <div style={{ background: "#f1f5f9", borderRadius: 6, height: 10, overflow: "hidden" }}>
                        <div style={{ height: "100%", background: count > 0 ? s.color : "#e2e8f0", width: `${widthPct}%`, borderRadius: 6, transition: "width 0.8s ease" }} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Closed lost footer */}
          {totalLost > 0 && (
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: "1px dashed #e2e8f0", display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 34, height: 34, borderRadius: 10, background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16 }}></div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#dc2626" }}>Closed  Not Interested</div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>{totalLost} lead{totalLost > 1 ? "s" : ""} closed as lost (preserved for future outreach)</div>
              </div>
              <div style={{ marginLeft: "auto", fontSize: 20, fontWeight: 900, color: "#dc2626" }}>{totalLost}</div>
            </div>
          )}
        </div>
      )}

      {/* TABLE VIEW */}
      {view === "table" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {STAGES.map(s => {
            const data  = stageMap[s.key] || {};
            const count = parseInt(data.active_count || 0);
            const total = parseInt(data.count        || 0);
            const value = data.total_value ? fmtValue(data.total_value) : null;

            return (
              <div key={s.key} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }}>
                  {s.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", marginBottom: 2 }}>{s.label}</div>
                  {value && <div style={{ fontSize: 11, color: "#64748b" }}>Pipeline value: {value}</div>}
                </div>
                <div style={{ textAlign: "right" }}>
                  <div style={{ fontSize: 22, fontWeight: 900, color: count > 0 ? s.color : "#cbd5e1" }}>{count}</div>
                  <div style={{ fontSize: 10, color: "#94a3b8" }}>active</div>
                </div>
              </div>
            );
          })}

          {/* Grand total */}
          <div style={{ background: "#1e293b", borderRadius: 14, padding: "14px 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>Total Pipeline Value</div>
              <div style={{ fontSize: 11, color: "#94a3b8" }}>All active stages</div>
            </div>
            <div style={{ fontSize: 20, fontWeight: 900, color: "#fbbf24" }}>{fmtValue(totalValue) || ""}</div>
          </div>
        </div>
      )}

      {view === "intelligence" && intelligence && (
        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
          <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, padding:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:12 }}>
              <div>
                <div style={{ fontSize:13, fontWeight:800, color:"#1e293b" }}>Pipeline Health</div>
                <div style={{ fontSize:11, color:"#94a3b8", marginTop:2 }}>Progress, activity, closure dates and contacts</div>
              </div>
              <div style={{ width:64, height:64, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", background:intelligence.health_score>=80?"#dcfce7":intelligence.health_score>=60?"#fef3c7":"#fee2e2", color:intelligence.health_score>=80?"#15803d":intelligence.health_score>=60?"#b45309":"#dc2626", fontSize:22, fontWeight:900 }}>{intelligence.health_score}</div>
            </div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2,minmax(0,1fr))", gap:8, marginTop:14 }}>
              {Object.entries(intelligence.dimensions || {}).map(([key,value]) => (
                <div key={key} style={{ background:"#f8fafc", borderRadius:10, padding:"9px 10px" }}>
                  <div style={{ fontSize:10, color:"#64748b", textTransform:"capitalize" }}>{key.replaceAll("_"," ")}</div>
                  <div style={{ fontSize:16, fontWeight:900, color:value>=20?"#15803d":value>=15?"#b45309":"#dc2626" }}>{value}/25</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(2,minmax(0,1fr))", gap:10 }}>
            {[
              ["Pipeline Value",fmtValue(intelligence.totals?.pipeline_value) || "0","#1d4ed8"],
              ["Weighted Forecast",fmtValue(intelligence.totals?.weighted_forecast) || "0","#15803d"],
              ["Active Leads",intelligence.totals?.active_leads || 0,accent],
              ["Won / Lost",`${intelligence.totals?.won || 0} / ${intelligence.totals?.lost || 0}`,"#7c3aed"],
            ].map(([label,value,color]) => (
              <div key={label} style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:14, padding:13 }}>
                <div style={{ fontSize:10, color:"#94a3b8" }}>{label}</div>
                <div style={{ fontSize:18, fontWeight:900, color, marginTop:3 }}>{value}</div>
              </div>
            ))}
          </div>

          <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, padding:16 }}>
            <div style={{ fontSize:13, fontWeight:800, color:"#1e293b", marginBottom:10 }}>Pipeline Hygiene</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(2,minmax(0,1fr))", gap:7 }}>
              {Object.entries(intelligence.hygiene || {}).map(([key,value]) => (
                <div key={key} style={{ display:"flex", justifyContent:"space-between", gap:8, background:value?"#fff7ed":"#f0fdf4", color:value?"#9a3412":"#166534", borderRadius:9, padding:"8px 9px", fontSize:10, fontWeight:700 }}>
                  <span style={{ textTransform:"capitalize" }}>{key.replaceAll("_"," ")}</span><b>{value}</b>
                </div>
              ))}
            </div>
          </div>

          <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, padding:16 }}>
            <div style={{ fontSize:13, fontWeight:800, color:"#1e293b", marginBottom:12 }}>Stage Conversion</div>
            {(intelligence.conversion || []).map(item => {
              const stage = STAGES.find(entry => entry.key === item.stage);
              return <div key={item.stage} style={{ marginBottom:10 }}>
                <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, marginBottom:4 }}><span style={{ fontWeight:700 }}>{stage?.label || item.stage}</span><span style={{ color:"#64748b" }}>{item.reached} reached · {item.conversion_rate}%</span></div>
                <div style={{ height:7, borderRadius:5, background:"#f1f5f9", overflow:"hidden" }}><div style={{ width:`${Math.min(100,item.conversion_rate)}%`, height:"100%", background:stage?.color || accent }} /></div>
              </div>;
            })}
          </div>

          <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, padding:16 }}>
            <div style={{ fontSize:13, fontWeight:800, color:"#1e293b", marginBottom:10 }}>Current Stage Ageing</div>
            {(intelligence.ageing || []).filter(item => item.count > 0).map(item => (
              <div key={item.stage} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"8px 0", borderBottom:"1px solid #f8fafc", fontSize:11 }}>
                <span style={{ color:"#475569", textTransform:"capitalize" }}>{item.stage.replaceAll("_"," ")} <small style={{ color:"#94a3b8" }}>({item.count})</small></span>
                <b style={{ color:item.average_days>14?"#dc2626":item.average_days>7?"#b45309":"#15803d" }}>{item.average_days} avg days</b>
              </div>
            ))}
          </div>

          <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, padding:16 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10, marginBottom:12 }}>
              <div><div style={{ fontSize:13, fontWeight:800, color:"#1e293b" }}>Lost-Lead Intelligence</div><div style={{ fontSize:10, color:"#94a3b8", marginTop:2 }}>Reasons, value and recovery</div></div>
              <div style={{ textAlign:"right" }}><div style={{ fontSize:17, fontWeight:900, color:"#dc2626" }}>{intelligence.lost_intelligence?.total || 0}</div><div style={{ fontSize:9, color:"#94a3b8" }}>lost · {fmtValue(intelligence.lost_intelligence?.total_value) || "0"}</div></div>
            </div>
            {(intelligence.lost_intelligence?.by_reason || []).map(item => (
              <div key={item.key} style={{ display:"flex", justifyContent:"space-between", gap:8, padding:"7px 0", borderBottom:"1px solid #f8fafc", fontSize:11 }}>
                <span style={{ color:"#475569", textTransform:"capitalize" }}>{item.key.replaceAll("_"," ")}</span>
                <span style={{ fontWeight:800, color:"#b91c1c" }}>{item.count} · {fmtValue(item.lost_value) || "0"}</span>
              </div>
            ))}
            {(intelligence.lost_intelligence?.competitors || []).length > 0 && <div style={{ marginTop:12, padding:"9px 10px", borderRadius:9, background:"#fff7ed", fontSize:10, color:"#9a3412" }}><b>Competitors:</b> {intelligence.lost_intelligence.competitors.map(item => `${item.key} (${item.count})`).join(", ")}</div>}
            <div style={{ marginTop:10, padding:"8px 10px", borderRadius:9, background:"#f0fdf4", color:"#166534", fontSize:10, fontWeight:700 }}>Reactivated leads: {intelligence.lost_intelligence?.reactivated || 0}</div>
          </div>

          <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, overflow:"hidden" }}>
            <div style={{ padding:"14px 16px", borderBottom:"1px solid #f1f5f9", fontSize:13, fontWeight:800 }}>Priority Risks</div>
            {(intelligence.priority_risks || []).length === 0 ? <div style={{ padding:24, textAlign:"center", color:"#15803d", fontSize:12 }}>No priority pipeline risks</div> : intelligence.priority_risks.map(item => (
              <div key={item.id} style={{ padding:"11px 16px", borderBottom:"1px solid #f8fafc" }}>
                <div style={{ display:"flex", justifyContent:"space-between", gap:8 }}><b style={{ fontSize:12, color:"#1e293b" }}>{item.institution_name}</b><span style={{ fontSize:10, color:"#64748b" }}>{fmtValue(item.value) || ""}</span></div>
                <div style={{ fontSize:10, color:"#64748b", marginTop:2 }}>{item.rep_name} · {(item.pipeline_stage || "").replaceAll("_"," ")}</div>
                <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginTop:6 }}>{item.reasons.map(reason => <span key={reason} style={{ padding:"2px 6px", borderRadius:6, background:"#fee2e2", color:"#b91c1c", fontSize:9, fontWeight:700 }}>{reason}</span>)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
